import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Photos, Project } from '../types';
import { toTitleCase } from '../functions/utils';
import { captureException } from '../utils/captureException';

interface PhotoSelectorGalleryProps {
  open: boolean;
  onClose: () => void;
  onSelectPhoto: (photo: Photos) => void;
  project: Project;
  currentZone: string;
  isMobile: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`photo-tabpanel-${index}`}
      aria-labelledby={`photo-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PhotoSelectorGallery: React.FC<PhotoSelectorGalleryProps> = ({
  open,
  onClose,
  onSelectPhoto,
  project,
  currentZone,
  isMobile,
}) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availablePhotos, setAvailablePhotos] = useState<Photos[]>([]);
  const [removedPhotos, setRemovedPhotos] = useState<Photos[]>([]);

  // Load photos when dialog opens
  useEffect(() => {
    if (open) {
      loadPhotos();
    }
  }, [open, project]);

  const loadPhotos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get all photos from the project
      const allPhotos = project.fotos || [];
      
      setAvailablePhotos(allPhotos);
      
      // Load removed photos from removed_fotos array
      const removedPhotos = project.removed_fotos || [];
      if (removedPhotos.length > 0) {
        setRemovedPhotos(removedPhotos);
      } else {
        setRemovedPhotos([]);
      }
      
    } catch (err) {
      captureException(err, { tags: { feature: 'photoSelectorGallery' }, extra: { action: 'loadPhotos', project_code: project?.project_code } });
      console.error('Error loading photos:', err);
      setError(t('photoSelector.failedToLoadPhotos'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelectPhoto = (photo: Photos) => {
    onSelectPhoto(photo);
    onClose();
  };

  const handleRestorePhoto = async (photo: Photos) => {
    // TODO: Implement restore functionality
    console.log('Restore photo:', photo);
  };

  const renderPhotoCard = (photo: Photos, isRemoved: boolean = false) => (
    <Card
      key={photo.id}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
      onClick={() => !isRemoved && handleSelectPhoto(photo)}
    >
      <CardMedia
        component="img"
        height="200"
        image={photo.gcs_thumbnail_path || photo.gcs_path}
        alt={`Photo from ${photo.zone}`}
        sx={{
          objectFit: 'cover',
        }}
      />
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('photoSelector.currentZone')} </Box>
                <Box component="span" sx={{ color: 'primary.main' }}>{ toTitleCase(photo.zone || '-')}</Box>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: isMobile ? '100vh' : '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {t('photoSelector.title')}
            </Typography>
            <Typography variant="h6">
              {currentZone}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="photo tabs"
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 2,
              },
              '& .MuiTab-root': {
                minWidth: 0,
                flex: 1,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                borderRight: 'none',
                borderColor: 'divider',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab 
              label={t('photoSelector.availablePhotos', { count: availablePhotos.length })} 
              id="photo-tab-0"
              aria-controls="photo-tabpanel-0"
            />
            {/* <Tab 
              label={t('photoSelector.removedPhotos', { count: removedPhotos.length })} 
              id="photo-tab-1"
              aria-controls="photo-tabpanel-1"
            /> */}
          </Tabs>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2, borderRadius: '0px !important' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <TabPanel value={tabValue} index={0}>
              {availablePhotos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('photoSelector.noPhotosAvailable')}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {availablePhotos.map((photo) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={photo.id}>
                      {renderPhotoCard(photo)}
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* <TabPanel value={tabValue} index={1}>
              {removedPhotos.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('photoSelector.noRemovedPhotos')}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {removedPhotos.map((photo) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={photo.id}>
                      {renderPhotoCard(photo, true)}
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel> */}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('photoSelector.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoSelectorGallery;
